import { createClient } from '@supabase/supabase-js'
import { supabaseConfig, isOnline, tables, syncConfig } from '../config/supabase.js'

// Cliente Supabase — criado sempre, independente do estado de conexão
export const supabase = createClient(supabaseConfig.url, supabaseConfig.key)

// Funções auxiliares
export function isSupabaseAvailable() {
  return supabase !== null
}

// Função para lidar com erros de rede
export function handleSupabaseError(error) {
  console.error('[Supabase] Erro:', error)
  
  if (!isOnline) {
    throw new Error('Sem conexão com a internet. Operando em modo offline.')
  }
  
  if (error.code === 'PGRST116') {
    throw new Error('Registro não encontrado.')
  }
  
  if (error.code === '23505') {
    throw new Error('Registro já existe.')
  }
  
  throw error
}

// Função de retry com backoff exponencial
export async function retryOperation(operation, maxAttempts = syncConfig.retryAttempts) {
  let lastError
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error
      
      if (attempt === maxAttempts) {
        throw handleSupabaseError(error)
      }
      
      // Esperar antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, syncConfig.retryDelay * attempt))
    }
  }
  
  throw lastError
}

// Funções de sincronização
export async function syncTable(tableName, localData, lastSyncTime = null) {
  if (!isSupabaseAvailable()) {
    throw new Error('Supabase não disponível')
  }
  
  try {
    // Construir query - buscar todos os dados (sem filtro de updated_at)
    const { data, error } = await retryOperation(() => 
      supabase.from(tableName).select('*')
    )
    
    if (error) throw error
    
    return data
  } catch (error) {
    throw handleSupabaseError(error)
  }
}

// Função para enviar dados para o Supabase
export async function uploadData(tableName, data) {
  if (!isSupabaseAvailable()) {
    throw new Error('Supabase não disponível')
  }
  
  try {
    const { data: result, error } = await retryOperation(() => 
      supabase.from(tableName).upsert(data, { onConflict: 'id' })
    )
    
    if (error) throw error
    
    return result
  } catch (error) {
    throw handleSupabaseError(error)
  }
}

// Função para deletar dados
export async function deleteData(tableName, id) {
  if (!isSupabaseAvailable()) {
    throw new Error('Supabase não disponível')
  }
  
  try {
    const { error } = await retryOperation(() => 
      supabase.from(tableName).delete().eq('id', id)
    )
    
    if (error) throw error
    
    return true
  } catch (error) {
    throw handleSupabaseError(error)
  }
}

// Função para buscar dados com paginação
export async function fetchData(tableName, options = {}) {
  if (!isSupabaseAvailable()) {
    throw new Error('Supabase não disponível')
  }

  const {
    limit = 50,
    offset = 0,
    orderBy = 'created_at',
    order = 'desc',
    filters = {}
  } = options

  try {
    // Selecionar explicitamente campos JSONB para garantir retorno
    let selectQuery = '*'
    if (tableName === 'compras_pendentes' || tableName === 'pedidos_ativo' || tableName === 'historico_pedidos') {
      selectQuery = '*, itens' // Garantir que itens (JSONB) seja retornado
    }

    let query = supabase.from(tableName).select(selectQuery)

    // Aplicar filtros
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query = query.eq(key, value)
      }
    })

    // Aplicar ordenação e paginação
    query = query.order(orderBy, { ascending: order === 'asc' })
      .range(offset, offset + limit - 1)

    const { data, error } = await retryOperation(() => query)

    if (error) throw error

    return data
  } catch (error) {
    throw handleSupabaseError(error)
  }
}

// Função para verificar status da conexão
export async function checkConnection() {
  if (!isSupabaseAvailable()) {
    return { connected: false, error: 'Supabase não disponível' }
  }
  
  try {
    const { data, error } = await supabase.from(tables.config).select('key').limit(1)
    
    if (error) throw error
    
    return { connected: true, data }
  } catch (error) {
    return { connected: false, error: error.message }
  }
}

// Listener para mudanças em tempo real
export function subscribeToTable(tableName, callback) {
  if (!isSupabaseAvailable()) {
    return null
  }
  
  // Nome único para o canal (evita conflito no React Strict Mode)
  const channelName = `${tableName}_changes_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  
  const channel = supabase
    .channel(channelName)
    .on('postgres_changes', 
      { event: '*', schema: 'public', table: tableName },
      callback
    )
    .subscribe()
  
  // Retornar função de unsubscribe
  return () => {
    supabase.removeChannel(channel)
  }
}

// Exportar tudo
export {
  tables,
  syncConfig,
  isOnline
}
